import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowRight } from 'lucide-react';

const PLAYBOOK = [
  { title: 'Who monitors', body: 'Every KojiruACSHook event that lands on Base is watched by a service that writes it to a durable record within 60 seconds of the block being final. When that service is running, the on-call engineer is Wayne Faulkner. When it is not running, an alert reaches Wayne within 5 minutes. Today, that service does not exist yet. See /readiness question 1.' },
  { title: 'Who responds to a contested slash', body: 'A contested slash is any event where the seller-agent operator emails wayne@kojiru.com within 72 hours of a fire and asserts the on-chain proof does not accurately capture the promise they registered. The response is not to reverse the slash. The response is to publish the operator\u2019s written objection, alongside the on-chain event, at /proofs/example-000001.json (or the equivalent real packet URL). The contract is final. The record is not one-sided.' },
  { title: 'How a monitor signature failure is handled', body: 'If the independent monitor that signs breach proofs returns an error, a malformed signature, or takes longer than 24 hours to sign, the buyer\u2019s claim is queued in a public backlog readable by anyone. Kojiru does not retry the monitor silently. The backlog itself is the accountability surface.' },
  { title: 'When the founder is unavailable', body: 'When Wayne is unavailable for longer than 24 hours (travel, sickness, family), an auto-responder on wayne@kojiru.com states so, with a return date. The contract continues to run. Support and dispute triage pause. This is not a scaled operation and we will not pretend otherwise.' },
  { title: 'What we owe an affected party', body: 'If a Kojiru component fails and demonstrably causes financial loss to a party that has posted a pledge or made a payment through a KYPA-attested flow, we will publish the failure, the root cause, and the remediation within 7 days. If the failure is our fault, we will make the party whole from our own funds. This is a written commitment, not a legal one, and it is enforceable only by the reputational cost of not honouring it.' },
];

export default function Ops() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Operations Playbook · Kojiru</title>
        <meta name="description" content="Kojiru's public operations playbook. Who monitors. Who responds. What we owe an affected party. Written, versioned, and hard to walk back." />
      </Helmet>
      <Navbar />

      <section className="pt-32 pb-14 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-6">
              OPERATIONS PLAYBOOK &middot; VERSION 0.1 &middot; PUBLIC DRAFT
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Who monitors. Who responds. What we owe you.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl">
              Every payment platform runs on people. The competent ones write down what those people do
              and publish it. This is our first draft. It is short because we are small. It will grow
              honestly, section by section, as we scale &mdash; not as we pretend to.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 px-6 border-b border-white/5 bg-amber-500/[0.04]">
        <div className="max-w-4xl mx-auto flex items-start gap-4">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
          <div>
            <div className="text-[10px] font-bold tracking-[2px] text-amber-300 mb-2">HONEST STATUS</div>
            <p className="text-slate-300 leading-relaxed">
              Kojiru is a founder-led operation today. There is no 24/7 rotation, no dedicated support
              inbox, no incident-response team. Every commitment on this page is one Wayne Faulkner can
              personally uphold as the entire operation. When we scale beyond that, this page will
              expand with named roles.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          {PLAYBOOK.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="border-l-2 border-cyan-500/40 pl-6 sm:pl-8" data-testid={`ops-section-${i}`}>
              <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-2">SECTION {i + 1}</div>
              <h3 className="text-xl sm:text-2xl font-black mb-4 leading-tight">{p.title}</h3>
              <p className="text-slate-300 leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-14 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 max-w-2xl mx-auto mb-6 leading-relaxed">
            This playbook is versioned. Every change is logged with a date. If a section
            weakens between versions, we will name the deletion in the changelog rather
            than quietly walking it back.
          </p>
          <Link to="/readiness"><Button size="lg" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="ops-to-readiness">See the readiness self-audit <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
